import { Button, Checkbox, FormControlLabel, Grid, Typography, Box } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useReservation } from '../reservation.context';
import { FormTitle } from '../../../components/FormTitle.component';
import { ContractButton } from '../components/contractButton.component';
import { PromptLangButton } from '../../../components/PromptLangButton.component';
import EmailIcon from '@material-ui/icons/Email';
import { baseUrl, get, patch, post } from '../../../fetch';
import { useAlerts } from '../../../hooks/useAlerts';
import { useReservationHash } from '../reservationHash.context';
import { useDomainPath } from '../../../auth/auth.context';
import DescriptionIcon from '@material-ui/icons/Description';

/**
 * Formulaire de gestion des documents de réservation
 * 
 * Ce composant organise l'affichage des documents :
 * - Contrat de location avec signature électronique
 * - Facture avec génération multilingue
 * - Gestion des emails de confirmation
 * 
 * Logique métier :
 * - Vérification de la validité DocuSign pour les contrats
 * - Gestion des demandes de signature électronique
 * - Génération de factures en plusieurs langues
 * - Envoi d'emails de confirmation
 * - Rafraîchissement automatique après actions
 * 
 * @returns Composant React du formulaire de documents
 */
export const DocumentsForm: React.FC = () => {
  return (
    <Grid container spacing={3}>
      <Grid
        container
        spacing={3}
        item
        xs={12}
        sm={6}
        alignContent={'flex-start'}
      >
        <Grid item xs={12}>
          <FormTitle>Contrat de location</FormTitle>
        </Grid>
        <Grid item xs={12}>
          <ContractForm />
        </Grid>
        <Grid item xs={12}>
          <FormTitle>Facture</FormTitle>
        </Grid>
        <Grid item xs={12}>
          <InvoiceForm />
        </Grid>
      </Grid>
    </Grid>
  );
};

/**
 * Formulaire de gestion du contrat de location
 * 
 * Ce composant gère :
 * - L'impression du contrat
 * - La demande de signature électronique via DocuSign
 * - L'envoi d'emails de confirmation
 * - La vérification de la validité DocuSign
 * 
 * Logique métier :
 * - Vérification de la configuration DocuSign pour l'activité
 * - Gestion de l'état des contrats (envoyé/non envoyé)
 * - Envoi d'emails avec gestion d'erreurs
 * - Rafraîchissement automatique après actions
 * 
 * @returns Composant React du formulaire de contrat
 */
const ContractForm: React.FC = () => {
  const { notify } = useAlerts();
  const { reservation, reload, save } = useReservation();
  const { errors } = useReservationHash();
  const reservationPath = useDomainPath('/reservation');
  const activityPath = useDomainPath('/activity');
  
  /**
   * États pour la gestion des contrats
   */
  const [isDocusignValid, setIsDocusignValid] = useState<boolean>(false);
  const [isContractSent, setIsContractSent] = useState<boolean>(false);
  const [isContractSigned, setIsContractSigned] = useState<boolean>(false);
  const [hasOneFlowContract, setHasOneFlowContract] = useState<boolean>(false);

  /**
   * Vérification de la validité DocuSign et de l'état du contrat
   */
  useEffect(() => {
    if(reservation.activity) {
      get<string>(activityPath+`/checkHaveValidDocusignContract/${reservation.activity}`).then(
        (isvalid: string) => {
          setIsDocusignValid(isvalid === 'true');
        }
      )
    }
    const contractSent = !reservation.documents || (reservation.documents && !reservation.documents.oneFlowContract?.contractId) ? false : true;
    setIsContractSent(contractSent);
    
    // Charger le statut du contrat OneFlow depuis l'API si réservation existe
    if (reservation._id) {
      get<{ contractId: string; state: CRM.OneFlowContractState } | null>(
        `${reservationPath}/${reservation._id}/contract/oneflow/status`
      ).then((contractStatus) => {
        if (contractStatus) {
          setHasOneFlowContract(true);
          setIsContractSigned(contractStatus.state === 'signed');
        } else {
          setHasOneFlowContract(false);
          setIsContractSigned(false);
        }
      }).catch(() => {
        setHasOneFlowContract(false);
        setIsContractSigned(false);
      });
    }
  }, [reservation.activity, activityPath, reservation.documents, reservation._id, reservationPath]);

  /**
   * Fonction d'envoi d'email de confirmation
   * 
   * @param lang Langue de l'email à envoyer
   */
  const sendConfirmationEmail = useCallback(
    async (lang: CRM.I18nLang) => {
      try {
        if (reservation._id) {
          await save();
          await post(
            `${reservationPath}/${reservation._id}/notifications?type=email&name=confirmation&lang=${lang}`,
          );
          notify(`Email envoyé`, 'success');
          setTimeout(reload, 2000);
        }
      } catch (err) {
        notify(`Impossible d'envoyer l'email`, 'error');
      }
    },
    [save, reload, notify, reservationPath, reservation._id],
  );

  /**
   * Fonction de demande de signature par email
   * 
   * Envoie une demande de signature électronique via DocuSign
   */
  const requestSignatureByEmail = useCallback(
    async () => {
      try {
        if (reservation._id) {
          await save();
          await post(
            `${reservationPath}/${reservation._id}/contract/emailSignatureRequest`,
          );
          notify(`Demande de signature par email envoyée`, 'success');
          setTimeout(reload, 2000);
        }
      } catch (err) {
        notify(`Impossible d'envoyer l'email de demande de signature`, 'error');
      }
    },
    [save, reload, notify, reservationPath, reservation._id],
  );

  const handleContractSignedChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSigned = event.target.checked;
      setIsContractSigned(newSigned);
      try {
        await patch(
          `${reservationPath}/${reservation._id}/contract/oneflow/status`,
          { signed: newSigned },
        );
        notify(newSigned ? `Contrat marqué comme signé` : `Contrat marqué comme non signé`, 'success');
        setTimeout(reload, 1000);
      } catch (err) {
        notify(`Impossible de mettre à jour le statut du contrat`, 'error');
        setIsContractSigned(!newSigned);
      }
    },
    [reload, notify, reservationPath, reservation._id],
  );

  return (
    <Grid container spacing={2}>
      {reservation.documents && (
        <Grid item xs={12}>
          <Typography>Contrat n°{reservation.documents.contractId}</Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <ContractButton>Imprimer le contrat</ContractButton>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center">
          {isDocusignValid === true && (
            <Box mr={2}>
              <Button
                onClick={() => { requestSignatureByEmail() }}
                startIcon={<EmailIcon />}
                disabled={errors.length > 0 || isContractSent}
                color="secondary"
                variant="contained"
                type="button"
              >
                {!isContractSent ? "Envoyer la demande de signature électronique" : "Demande de signature électronique envoyée"}
              </Button>
            </Box>
          )}
          {hasOneFlowContract && reservation._id && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isContractSigned}
                  onChange={handleContractSignedChange}
                  color="primary"
                />
              }
              label="Contrat Signé"
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PromptLangButton
          defaultLang={reservation.customer?.lang}
          onClick={sendConfirmationEmail}
          startIcon={<EmailIcon />}
          disabled={errors.length > 0}
        >
          Envoyer l'email de confirmation
        </PromptLangButton>
      </Grid>
    </Grid>
  );
};

/**
 * Formulaire de gestion de la facture
 * 
 * Ce composant gère :
 * - La génération de factures multilingues
 * - L'impression des factures
 * - L'affichage du numéro de facture
 * 
 * Logique métier :
 * - Génération de factures selon la langue du client
 * - Ouverture dans une nouvelle fenêtre pour impression
 * - Rafraîchissement automatique après génération
 * 
 * @returns Composant React du formulaire de facture
 */
const InvoiceForm: React.FC = () => {
  const { reservation, reload, save } = useReservation();
  const { errors } = useReservationHash();
  const reservationPath = useDomainPath('/reservation');

  /**
   * Fonction de génération de facture
   * 
   * @param lang Langue de la facture à générer
   */
  const generateInvoice = useCallback(
    (lang: CRM.I18nLang) => {
      if (reservation._id) {
        save().then(() => {
          var win = window.open(
            `${baseUrl}/${reservationPath}/${reservation._id}/invoice?lang=${lang}`,
            '_blank',
          );
          if(win) {
            //win.print();
          }
          // reload to actualize fields that could be updated on the backend, like the contract id
          setTimeout(reload, 2000);
        });
      }
    },
    [reload, save, reservationPath, reservation._id],
  );

  return (
    <Grid container spacing={2}>
      {reservation.documents && (
        <Grid item xs={12}>
          <Typography>Facture n°{reservation.documents.invoiceId}</Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <PromptLangButton
          defaultLang={reservation.customer?.lang}
          onClick={generateInvoice}
          startIcon={<DescriptionIcon />}
          disabled={errors.length > 0}
        >
          Imprimer la facture
        </PromptLangButton>
      </Grid>
    </Grid>
  );
};
